import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertCircle, Plus, X, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const ProblemTypeSearch = ({
    value,
    onChange,
    problemTypes: externalProblemTypes,
    placeholder = 'Search problem type...',
    allowCreate = true,
    className = '',
    inputClassName = '',
}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [allProblemTypes, setAllProblemTypes] = useState(externalProblemTypes || []);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', priority: 'medium' });
    const [creating, setCreating] = useState(false);

    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const modalOpenRef = useRef(false);

    // Parse comma-separated value into array of selected strings
    const selectedItems = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];

    const setModal = (val) => {
        modalOpenRef.current = val;
        setModalOpen(val);
    };

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });

    useEffect(() => {
        if (!externalProblemTypes || externalProblemTypes.length === 0) {
            axios.get(`${API}/problem-types`, getAuthHeaders())
                .then(r => setAllProblemTypes(r.data))
                .catch(() => { });
        }
    }, []); // eslint-disable-line

    useEffect(() => {
        if (externalProblemTypes) setAllProblemTypes(externalProblemTypes);
    }, [externalProblemTypes]);

    // Filter results as user types (exclude already selected)
    useEffect(() => {
        const available = allProblemTypes.filter(p => !selectedItems.includes(p.name));
        if (!query.trim()) { setResults(available.slice(0, 10)); return; }
        const q = query.toLowerCase();
        setResults(
            available
                .filter(p => p.name?.toLowerCase().includes(q))
                .slice(0, 10)
        );
    }, [query, allProblemTypes, value]); // value dependency ensures selected items disappear from list

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (modalOpenRef.current) return;
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelect = (pt) => {
        if (selectedItems.includes(pt.name)) return;
        const next = [...selectedItems, pt.name];
        onChange(next.join(', '));
        setQuery('');
        setOpen(false);
    };

    const handleRemove = (nameToRemove, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const next = selectedItems.filter(v => v !== nameToRemove);
        onChange(next.join(', '));
    };

    const openCreateModal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setCreateForm({ name: query.trim(), priority: 'medium' });
        setOpen(false);
        setModal(true);
    };

    const handleCreateProblemType = async (e) => {
        e.preventDefault();
        if (!createForm.name.trim()) return;
        setCreating(true);
        try {
            const res = await axios.post(`${API}/problem-types`, createForm, getAuthHeaders());
            const newPT = res.data;
            setAllProblemTypes(prev => [...prev, newPT].sort((a, b) => a.name.localeCompare(b.name)));
            toast.success(`Problem Type "${newPT.name}" created`);
            setModal(false);
            setCreateForm({ name: '', priority: 'medium' });
            handleSelect(newPT);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create problem type');
        } finally {
            setCreating(false);
        }
    };

    const noResults = !!query.trim() && results.length === 0;
    const showDropdown = open && (results.length > 0 || (noResults && allowCreate));

    return (
        <>
            <div ref={containerRef} className={`relative ${className}`}>
                {/* Selected Tags Area */}
                {selectedItems.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {selectedItems.map(item => (
                            <span key={item} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-md text-xs font-medium">
                                {item}
                                <button
                                    type="button"
                                    onClick={(e) => handleRemove(item, e)}
                                    className="hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* Search input */}
                <div className="relative">
                    <AlertCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => { setOpen(true); }}
                        placeholder={selectedItems.length > 0 ? "Add another problem type..." : placeholder}
                        className={`pl-9 pr-8 bg-white ${inputClassName}`}
                        autoComplete="off"
                    />
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                {/* Dropdown results */}
                {showDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                        {results.length > 0 && (
                            <ul className="max-h-60 overflow-y-auto divide-y divide-gray-50">
                                {results.map(p => (
                                    <li
                                        key={p.id}
                                        className={`flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-blue-50 transition-colors`}
                                        onMouseDown={(e) => { e.preventDefault(); handleSelect(p); }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4 text-gray-400 shrink-0" />
                                            <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                                        </div>
                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${p.priority === 'critical' ? 'bg-red-100 text-red-700' :
                                            p.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                                p.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-green-100 text-green-700'
                                            }`}>
                                            {p.priority}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {noResults && (
                            <div className="px-3 py-2.5 text-sm text-gray-500 border-b border-gray-100">
                                No match for <strong>"{query}"</strong>
                            </div>
                        )}

                        {noResults && allowCreate && (
                            <button
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-3 hover:bg-emerald-50 text-emerald-700 font-medium text-sm transition-colors"
                                onMouseDown={openCreateModal}
                            >
                                <Plus className="w-4 h-4 shrink-0" />
                                <span>Create new problem type <strong>"{query}"</strong></span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ── Create Problem Type Modal ── */}
            <Dialog open={modalOpen} onOpenChange={(o) => { if (!creating) setModal(o); }}>
                <DialogContent className="sm:max-w-sm" onPointerDownOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-emerald-600" />
                            Create New Problem Type
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateProblemType} className="space-y-4 pt-1">
                        <div className="space-y-2">
                            <Label htmlFor="pt-name">
                                Problem Type Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="pt-name"
                                placeholder="e.g. Server Outage"
                                value={createForm.name}
                                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                required
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="pt-priority">
                                Default Priority Level <span className="text-red-500">*</span>
                            </Label>
                            <Select
                                value={createForm.priority}
                                onValueChange={(val) => setCreateForm({ ...createForm, priority: val })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select priority..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="critical">Critical</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex gap-2 pt-1">
                            <Button
                                type="submit"
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                disabled={creating}
                            >
                                {creating
                                    ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating…</>
                                    : 'Create & Select'
                                }
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setModal(false)}
                                disabled={creating}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ProblemTypeSearch;
